<!-- This dialog is meant to be placed on the main page and then opened
with dynamic title and content from any place in the app.-->

<template>
  <v-dialog
    v-model="show"
    :persistent="persistent"
    :width="maxDialogWidth || $store.state.common.largeDialogWidth"
    :fullscreen="allowMobileFullscreen && $vuetify.breakpoint.xsOnly"
  >
    <v-card>
      <v-card-title class="pb-0">
        <v-layout class="headline">
          <v-layout
            align-center
            justify-center
          >
            <v-icon
              v-if="titleIcon"
              large
              :class="titleIconClass"
              class="my-1 mr-3"
            >
              {{ titleIcon }}
            </v-icon>
            <span class="text-xs-center">{{ title }}</span>
            <v-btn
              v-if="showAbort"
              data-cy="dynamic-dialog-btn-abort"
              class="ml-auto mr-0 my-0"
              :disabled="loading"
              flat
              icon
              :title="$t('close')"
              @click="
                boundOnAbort ? boundOnAbort() : null;
                show = false;
              "
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <div v-html="content" />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <btn-ok
          v-if="onConfirm"
          :loading="loading"
          data-cy="dynamic-dialog-btn-confirm"
          round
          :icon="confirmIcon"
          :label-with-icon="!!confirmIcon && !!confirmLabel"
          :label="confirmLabel"
          @click="boundOnConfirm"
        />
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import BtnOk from '@/components/ButtonOk';

const defaults = {
  allowMobileFullscreen: true,
  confirmIcon: 'check',
  confirmLabel: null,
  content: '',
  onAbort: undefined,
  onConfirm: undefined,
  persistent: false,
  showAbort: true,
  title: '',
  titleIcon: null,
  titleIconClass: 'msr--text',

  loading: false,
  maxDialogWidth: null,
  show: false,
};

export default {
  components: {
    BtnOk,
  },
  data() {
    return {
      allowMobileFullscreen: defaults.allowMobileFullscreen,
      confirmIcon: defaults.confirmIcon,
      confirmLabel: defaults.confirmLabel,
      content: defaults.content,
      onAbort: defaults.onAbort,
      onConfirm: defaults.onConfirm,
      persistent: defaults.persistent,
      showAbort: defaults.showAbort,
      title: defaults.title,
      titleIcon: defaults.titleIcon,
      titleIconClass: defaults.titleIconClass,

      show: defaults.show,
      loading: defaults.loading,
      maxDialogWidth: defaults.maxDialogWidth,
    };
  },
  computed: {
    /**
     * Since onConfirm() is passed from a different context, we need to
     * bind it to make 'this' in it refer to this dialog component.
     */
    boundOnConfirm() {
      if (!this.onConfirm) {
        return undefined;
      }
      const boundFunc = this.onConfirm.bind(this);
      return boundFunc;
    },
    boundOnAbort() {
      if (!this.onAbort) {
        return undefined;
      }
      const boundFunc = this.onAbort.bind(this);
      return boundFunc;
    },
  },
  mounted() {
    this.registerGlobalSignalHandlers();
  },
  destroyed() {
    this.unregisterGlobalSignalHandlers();
  },
  methods: {
    setFromOptions_(opts) {
      this.allowMobileFullscreen =
        opts.allowMobileFullscreen !== undefined
          ? opts.allowMobileFullscreen
          : defaults.allowMobileFullscreen;
      this.confirmIcon =
        opts.confirmIcon === null
          ? opts.confirmIcon
          : opts.confirmIcon || defaults.confirmIcon;
      this.confirmLabel = opts.confirmLabel || defaults.confirmLabel;
      this.content = opts.content || defaults.content;
      this.loading = opts.loading || defaults.loading;
      this.maxDialogWidth = opts.maxDialogWidth || defaults.maxDialogWidth;
      this.onAbort = opts.onAbort || defaults.onAbort;
      this.onConfirm = opts.onConfirm || defaults.onConfirm;
      this.persistent = opts.persistent || defaults.persistent;
      this.showAbort =
        opts.showAbort !== undefined ? opts.showAbort : defaults.showAbort;
      this.title = opts.title || defaults.title;
      this.titleIcon = opts.titleIcon || defaults.titleIcon;
      this.titleIconClass = opts.titleIconClass || defaults.titleIconClass;
    },
    reset() {
      this.allowMobileFullscreen = defaults.allowMobileFullscreen;
      this.confirmIcon = defaults.confirmIcon;
      this.confirmLabel = defaults.confirmLabel;
      this.content = defaults.content;
      this.loading = defaults.loading;
      this.maxDialogWidth = defaults.maxDialogWidth;
      this.onAbort = defaults.onAbort;
      this.onConfirm = defaults.onConfirm;
      this.persistent = defaults.persistent;
      this.showAbort = defaults.showAbort;
      this.title = defaults.title;
      this.titleIcon = defaults.titleIcon;
      this.titleIconClass = defaults.titleIconClass;
    },
    showDialog_(opts) {
      this.setFromOptions_(opts);
      this.show = true;
    },
    hideDialog_() {
      this.show = false;
      this.reset();
    },
    registerGlobalSignalHandlers() {
      this.$bus.$on('show-dynamic-dialog', (opts) => this.showDialog_(opts));
      this.$bus.$on('hide-dynamic-dialog', () => this.hideDialog_());
    },
    unregisterGlobalSignalHandlers() {
      this.$bus.$off('show-dynamic-dialog');
      this.$bus.$off('hide-dynamic-dialog');
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/DynamicDialog.vue